'use client';
import { __rest } from '../../node_modules/tslib/tslib.es6.js';
import { jsx, Fragment } from 'react/jsx-runtime';
import { useReactTable, getCoreRowModel, getFilteredRowModel, getExpandedRowModel, getGroupedRowModel, getSortedRowModel, getPaginationRowModel } from '@tanstack/react-table';
import { createContext, useContext, useState, useMemo, useEffect } from 'react';
import { withTitan } from '../../hocs/withTitan/withTitan.js';
import { TableCheckbox } from './TableCheckbox.js';

const TableContext = createContext(undefined);
const useTable = () => {
  const context = useContext(TableContext);
  if (context === undefined) {
    throw new Error('useTable must be used within a TableProvider');
  }
  return context;
};
const simpleFilter = (value, filter) => {
  if (!filter) return true;
  const search = filter.toLowerCase();
  if (typeof value === 'number') {
    value = String(value);
  }
  if (!value) {
    value = '';
  }
  return value.toLowerCase().includes(search);
};
const simpleFilterFn = (row, columnId, filterValue) => {
  return simpleFilter(row.getValue(columnId), filterValue);
};
const TableProvider = /*#__PURE__*/withTitan(_a => {
  var {
      columns: initialColumns,
      data = [],
      onStateChange,
      totalCount,
      customGroupHeader,
      pageSizeOptions = [10, 25, 50],
      hasPagination = false,
      enableRowSelection = false,
      shouldIndentGrouping = false,
      enableSortingRemoval = false,
      enableSorting,
      globalFilterFn,
      sortingFns,
      children,
      labelPageSize,
      labelPageNext,
      labelPagePrevious,
      labelPageSizeOption,
      labelGroupCollapse = 'Groep dichtklappen',
      labelGroupExpand = 'Groep openklappen',
      labelPageCurrent,
      labelColumnSort,
      labelSelectAll = 'Selecteer alles',
      labelSelect = 'Selecteer',
      isLoading = false,
      isDraggable,
      handleDragEnd,
      loaderHeight,
      defaultColumn,
      onRowClick,
      onRowSelectionChange,
      testId = 'tableProvider',
      state,
      initialState,
      manualFiltering,
      manualSorting,
      paginateExpandedRows,
      pageCount = totalCount && (state === null || state === void 0 ? void 0 : state.pagination) ? Math.ceil(totalCount / state.pagination.pageSize) : undefined
    } = _a,
    rest = __rest(_a, ["columns", "data", "onStateChange", "totalCount", "customGroupHeader", "pageSizeOptions", "hasPagination", "enableRowSelection", "shouldIndentGrouping", "enableSortingRemoval", "enableSorting", "globalFilterFn", "sortingFns", "children", "labelPageSize", "labelPageNext", "labelPagePrevious", "labelPageSizeOption", "labelGroupCollapse", "labelGroupExpand", "labelPageCurrent", "labelColumnSort", "labelSelectAll", "labelSelect", "isLoading", "isDraggable", "handleDragEnd", "loaderHeight", "defaultColumn", "onRowClick", "onRowSelectionChange", "testId", "state", "initialState", "manualFiltering", "manualSorting", "paginateExpandedRows", "pageCount"]);
  const [rowSelection, setRowSelection] = useState({});
  const [selectedRows, setSelectedRows] = useState([]);
  const groupByColumns = useMemo(() => {
    if (state === null || state === void 0 ? void 0 : state.grouping) return state.grouping;
    return (initialState === null || initialState === void 0 ? void 0 : initialState.grouping) || [];
  }, [initialState === null || initialState === void 0 ? void 0 : initialState.grouping, state === null || state === void 0 ? void 0 : state.grouping]);
  const columnVisibility = useMemo(() => groupByColumns.reduce((all, name) => Object.assign(Object.assign({}, all), {
    [name]: false
  }), {}), [groupByColumns]);
  const columns = useMemo(() => {
    if (!enableRowSelection) {
      return initialColumns;
    }
    return [{
      id: 'select',
      size: 1,
      header: ({
        table
      }) => {
        return jsx(TableCheckbox, {
          isChecked: table.getIsAllRowsSelected(),
          isSubtlyChecked: table.getIsSomeRowsSelected(),
          label: labelSelectAll,
          shouldRender: !groupByColumns.length,
          testId: `${testId}SelectAll`,
          onChange: () => table.toggleAllPageRowsSelected()
        });
      },
      cell: ({
        row
      }) => {
        const isSubRow = row.id.includes('.');
        if (!row.getCanSelect()) {
          return jsx(Fragment, {});
        }
        return jsx(TableCheckbox, {
          isChecked: row.getIsSelected(),
          isSubtlyChecked: row.getIsSomeSelected(),
          label: labelSelect,
          shouldRender: !isSubRow,
          testId: `${testId}Select`,
          onChange: checked => row.toggleSelected(checked)
        });
      }
    }, ...initialColumns];
  }, [enableRowSelection, groupByColumns.length, initialColumns, labelSelectAll, labelSelect, testId]);
  const instance = useReactTable(Object.assign(Object.assign(Object.assign({
    columns,
    data,
    initialState,
    state: Object.assign({
      columnVisibility,
      rowSelection
    }, state),
    pageCount,
    manualPagination: !hasPagination || !!(state === null || state === void 0 ? void 0 : state.pagination)
  }, hasPagination ? {
    paginateExpandedRows: paginateExpandedRows !== null && paginateExpandedRows !== void 0 ? paginateExpandedRows : !groupByColumns.length
  } : {}), {
    enableSortingRemoval,
    manualSorting: manualSorting !== null && manualSorting !== void 0 ? manualSorting : !!(state === null || state === void 0 ? void 0 : state.sorting),
    manualFiltering: manualFiltering !== null && manualFiltering !== void 0 ? manualFiltering : !!(state === null || state === void 0 ? void 0 : state.globalFilter),
    globalFilterFn: globalFilterFn || simpleFilterFn,
    enableRowSelection,
    onRowSelectionChange: state => {
      setRowSelection(state);
      onRowSelectionChange === null || onRowSelectionChange === void 0 ? void 0 : onRowSelectionChange(state);
      if (rest.getRowId) {
        const addedIds = Object.keys(typeof state === 'function' ? state({}) : state);
        const addedRows = Object.values(addedIds).map(id => data.find((row, index) => rest.getRowId(row, index) === id));
        const nextRowSelectionState = typeof state === 'function' ? state(rowSelection) : state;
        const ids = Object.keys(nextRowSelectionState);
        setSelectedRows(selectedRows => {
          return selectedRows.concat(addedRows).filter((row, index) => ids.includes(rest.getRowId(row, index)));
        });
      }
    },
    getSubRows: row => row.subRows,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    defaultColumn: Object.assign({
      size: 9999
    }, defaultColumn)
  }), rest));
  // Get number of rows from react-table
  const calculatedTotalCount = totalCount || instance.getPrePaginationRowModel().rows.length;
  const instanceState = instance.getState();
  useEffect(() => {
    if (onStateChange) onStateChange(instanceState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceState.pagination.pageIndex, instanceState.pagination.pageSize, instanceState.sorting, instanceState.globalFilter]);
  const hasGrouping = !!groupByColumns.length;
  const context = useMemo(() => ({
    labelPageCurrent,
    customGroupHeader,
    labelGroupCollapse,
    labelGroupExpand,
    shouldIndentGrouping,
    isLoading,
    loaderHeight,
    labelPageNext,
    labelPageSize,
    labelPageSizeOption,
    labelColumnSort,
    pageSizeOptions,
    labelPagePrevious,
    totalCount: calculatedTotalCount,
    hasPagination,
    rowSelection,
    hasRowSelection: !!enableRowSelection,
    hasGrouping,
    onRowClick,
    isDraggable: !hasGrouping && isDraggable,
    handleDragEnd,
    selectedRows: selectedRows.length > 0 ? selectedRows : instance.getSelectedRowModel().flatRows.map(row => row.original)
  }), [labelPageCurrent, customGroupHeader, labelGroupCollapse, labelGroupExpand, shouldIndentGrouping, isLoading, loaderHeight, labelPageNext, labelPageSize, labelPageSizeOption, labelColumnSort, pageSizeOptions, labelPagePrevious, calculatedTotalCount, hasPagination, rowSelection, enableRowSelection, hasGrouping, onRowClick, isDraggable, handleDragEnd, selectedRows, instance]);
  return (
    // eslint-disable-next-line react/jsx-no-constructed-context-values
    jsx(TableContext.Provider, {
      value: Object.assign(Object.assign({}, context), {
        instance
      }),
      children: children instanceof Function ? children(Object.assign(Object.assign({}, context), {
        instance
      })) : children
    })
  );
}, {
  name: 'TableProvider'
});

export { TableContext, TableProvider, useTable };
//# sourceMappingURL=TableProvider.js.map
